import { GP_SLOT_VALUES, TYPE_OF_PURCHASE } from '../../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../../constants/FormFieldsConstants';
import {
    DetailedBookingSOVData, SlotOptionType, SlotPackage, SovActiveModalType,
    SOVContextualNodeData
} from '../../interface/DetailedBookingInterface';
import { getProcessedValue } from '../helpers';
import { checkPercentageValidation, checkRoValueValidation, getSelectedSlotData } from './helpers';

interface HandleInputChangeProps {
    setFormData: React.Dispatch<React.SetStateAction<DetailedBookingSOVData>>;
    gpSlot: string;
    slotPackagesData: SlotPackage[];
    roValue: number;
    field: string;
    value: string;
    typeOfPurchase: string;
    setActiveModal: SovActiveModalType;
    selectedOption?: SlotOptionType;
    setContextualNodes?: React.Dispatch<React.SetStateAction<SOVContextualNodeData[]>>;
    isApprovedBooking?: boolean;
    existingPercentage?: number;
    isEditMode?: boolean;
    isWaitlistEnabled?: boolean;
}

export const handleInputChange = ({
    setFormData,
    gpSlot,
    slotPackagesData,
    roValue,
    field,
    value,
    typeOfPurchase,
    setActiveModal,
    selectedOption,
    setContextualNodes,
    isApprovedBooking,
    existingPercentage,
    isEditMode,
    isWaitlistEnabled,
}: HandleInputChangeProps
) => {
    const numericFields: string[] = [
        FORM_FIELDS.SOV_PERCENTAGE_FOR_GP,
        FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM,
        FORM_FIELDS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP,
        FORM_FIELDS.IMPRESSION_BOOKED,
        FORM_FIELDS.VALUE_BOOKED,
    ];
    const processedValue = getProcessedValue(numericFields, field, value);

    if (field === FORM_FIELDS.SOV_PERCENTAGE_FOR_GP) {
        const sovPercentage = processedValue as number;

        if (checkPercentageValidation(sovPercentage, setActiveModal, gpSlot, slotPackagesData, selectedOption, isApprovedBooking, existingPercentage, isWaitlistEnabled)) return;

        if (typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV) {
            let calculatedValue = 0;
            let calculatedImpressions = 0;

            if (selectedOption?.type === GP_SLOT_VALUES.NON_CONTEXTUAL) {
                const selectedSlotData = getSelectedSlotData(gpSlot, slotPackagesData, selectedOption);
                if (selectedSlotData && selectedSlotData.overAllImpressions && selectedSlotData.cpm) {
                    calculatedImpressions = Math.floor((selectedSlotData.overAllImpressions * sovPercentage) / 100);
                    calculatedValue = Math.floor((calculatedImpressions * selectedSlotData.cpm) / 1000);
                }
            } else if (selectedOption?.type === GP_SLOT_VALUES.CONTEXTUAL) {
                const selectedPackageData = slotPackagesData.find(
                    (pkg) => pkg.slotPackageName === gpSlot
                );
                if (selectedPackageData && selectedPackageData.segments.length > 0) {
                    const totalOverallValue = selectedPackageData.segments.reduce(
                        (sum, segment) => sum + segment.overAllValue,
                        0
                    );
                    calculatedValue = (totalOverallValue / 100) * sovPercentage;
                }
            }

            if (!isEditMode && calculatedValue > 0 && checkRoValueValidation(calculatedValue, roValue, setActiveModal))
                return;

            setFormData((prev) => ({
                ...prev,
                [field]: sovPercentage,
                impressionsForNonContextual: calculatedImpressions,
                valueBlockedByLineItem: calculatedValue,
            }));
            return;
        }

        const selectedSlotData = getSelectedSlotData(gpSlot, slotPackagesData, selectedOption);
        if (
            selectedOption?.type === GP_SLOT_VALUES.NON_CONTEXTUAL &&
            selectedSlotData &&
            selectedSlotData.cpm && selectedSlotData.overAllImpressions
        ) {
            const calculatedImpressions = Math.floor((selectedSlotData.overAllImpressions * sovPercentage) / 100);
            const calculatedValue = Math.floor((calculatedImpressions * selectedSlotData.cpm) / 1000);

            if (!isEditMode && checkRoValueValidation(calculatedValue, roValue, setActiveModal)) return;

            setFormData((prev) => ({
                ...prev,
                [field]: sovPercentage,
                impressionBooked: calculatedImpressions,
                valueBlockedByLineItem: calculatedValue,
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, [field]: sovPercentage }));
        return;
    }

    if (
        !isEditMode &&
        field === FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM &&
        checkRoValueValidation(+processedValue, roValue, setActiveModal)
    )
        return;

    if (field === FORM_FIELDS.GP_SLOT) {
        setFormData((prev) => ({
            ...prev,
            [field]: processedValue as string,
            valueBlockedByLineItem: 0,
            sovPercentageForGP: 0,
        }));

        if (setContextualNodes) setContextualNodes([{
            id: Date.now().toString(),
            contextualNode: '',
            sovPercentageForContextualGP: 0,
            impressionBooked: 0,
            valueBooked: 0,
        }]);
        return;
    }

    setFormData((prev) => ({ ...prev, [field]: processedValue }));
};
